import { z } from 'zod';

import type {
  AtividadeCasa,
  Autonomia,
  Boletim,
  Chegada,
  Comportamento,
  Desempenho,
  Foco,
  HorarioDoDiaType,
} from './enums';

/**
 * Agregação por período sobre `RegistroAula` (sem migration). Fora de escopo: atrasos (`Chegada` não tem valor
 * numérico) e "Feedback semana" (issue #17).
 */
export const PeriodoHistoricoEnum = z.enum(['DIA', 'SEMANA', 'MES', 'TUDO']);
export type PeriodoHistorico = z.infer<typeof PeriodoHistoricoEnum>;

export const HistoricoQuery = z.object({
  periodo: PeriodoHistoricoEnum,
});
export type HistoricoQueryType = z.infer<typeof HistoricoQuery>;

/** Dado bruto de um horário de hoje, com nomes resolvidos e conteúdos trabalhados (card da aba "Dia"). */
export type RegistroDoDiaOutput = {
  id: string;
  horarioId: string;
  horarioPrevisto: HorarioDoDiaType;
  professorNome: string;
  professorCor: string;
  materiaNome: string;
  chegada: Chegada | null;
  boletim: Boletim | null;
  atividadeCasa: AtividadeCasa | null;
  foco: Foco | null;
  autonomia: Autonomia | null;
  comportamento: Comportamento | null;
  desempenho: Desempenho | null;
  relatorioTexto: string | null;
  relatorioEnviadoEm: Date | null;
  conteudos: { id: string; nome: string }[];
};
export type RegistroDoDiaOutputType = RegistroDoDiaOutput;

/** Como `RegistroDoDiaOutput`, com a data: a aba "Histórico completo" lista vários dias na mesma página. */
export type RegistroHistoricoOutput = RegistroDoDiaOutput & {
  data: Date;
};
export type RegistroHistoricoOutputType = RegistroHistoricoOutput;

/** Fixo no servidor, nunca escolhido pelo cliente. */
export const HISTORICO_COMPLETO_PAGINA_TAMANHO = 30;

export const HistoricoCompletoQuery = z.object({
  pagina: z.coerce.number().int().min(1).default(1),
  dataInicio: z.coerce.date().optional(),
  dataFim: z.coerce.date().optional(),
});
export type HistoricoCompletoQueryType = z.infer<typeof HistoricoCompletoQuery>;

/** Log bruto, mais recente primeiro, paginado por offset (a UI salta pra página N). */
export type HistoricoCompletoOutput = {
  registros: RegistroHistoricoOutput[];
  pagina: number;
  totalPaginas: number;
  total: number;
};
export type HistoricoCompletoOutputType = HistoricoCompletoOutput;

export type HistoricoAcompanhamentoOutput = {
  previstas: number;
  realizadas: number;
  presencaPercentual: number;
  tarefasFeitasPercentual: number | null;
  mediaFoco: number | null;
  mediaAutonomia: number | null;
  mediaComportamento: number | null;
  mediaDesempenho: number | null;
  evolucao: {
    foco: number | null;
    autonomia: number | null;
    comportamento: number | null;
    desempenho: number | null;
  };
  // Só com `periodo === 'DIA'`: um item por horário ativo de hoje (linha "virtual" se não há registro). `null` nos outros.
  registrosDoDia: RegistroDoDiaOutput[] | null;
};
export type HistoricoAcompanhamentoOutputType = HistoricoAcompanhamentoOutput;
